import { PHASE_SLUG, sortByPhase } from "stores/phase/utils"
import querySo from "stores/route/query"
import { getItemsFiltered } from "stores/tag/utils"
import { Crop, Day, Schema } from "types/Crop"
import { CropRecipe } from "types/CropRecipe"
import { PhasePolicy, Recipe } from "types/Recipe"
import { CombinedDay, ExtendedGroupPhase, POLICY_NAMES } from "./types"



/**  
 * restituisce un nuovo group-phase
 */
export function getNewGroupPhase(phase = PHASE_SLUG.BATCH): ExtendedGroupPhase {
    return {
        phase, // slug
        collapsed: false,
        days: [getNewDay()],
        daysSelect: [],

        // RECIPE
        shrink: { name: POLICY_NAMES.DEFAULT, dayRef: 1 },
        expand: { name: POLICY_NAMES.DEFAULT, dayRef: 1 },

        // CROP
        ratio: 1,
        shrinkDays: 0,
        expandDays: 0,
    }
}

/**
 * Genera un array di GroupPhase combinando i dati da Recipe e CropRecipe
 * Prende le informazioni delle fasi dal Recipe e le combina con i task dal CropRecipe
 */
export function daysToPhasesGroup(
    cropRecipe: Partial<CropRecipe>
): ExtendedGroupPhase[] {
    const recipe = cropRecipe?.recipe ?? null
    const recipeDays = recipe?.days ?? []
    const cropDays = cropRecipe?.days ?? []
    const rearrangementSchema = cropRecipe?.rearrangementSchema ?? []

    // GROUP in base alla PHASE dei DAYS della RECIPE
    const recipePhaseMap = new Map<string, CombinedDay[]>()
    recipeDays
        .sort((dayA, dayB) => (dayA.position ?? 0) - (dayB.position ?? 0))
        .forEach(day => {
            const phase = day.phase && day.phase.length > 0 ? day.phase : PHASE_SLUG.BATCH
            if (!recipePhaseMap.has(phase)) recipePhaseMap.set(phase, [])
            recipePhaseMap.get(phase)!.push(day)
        })

    // GROUP in base alla PHASE dei DAYS del CROP
    const cropPhaseMap = new Map<string, Day[]>()
    cropDays
        .sort((dayA, dayB) => (dayA.position ?? 0) - (dayB.position ?? 0))
        .forEach(day => {
            const phase = day.phase && day.phase.length > 0 ? day.phase : PHASE_SLUG.BATCH
            if (!cropPhaseMap.has(phase)) cropPhaseMap.set(phase, [])
            cropPhaseMap.get(phase)!.push(day)
        })

    // Ottieni tutte le PHASES presenti
    const allPhases = [...cropPhaseMap.keys()]
    if ( recipePhaseMap.has(PHASE_SLUG.PRE_CYCLE) ) allPhases.unshift(PHASE_SLUG.PRE_CYCLE)
    if ( recipePhaseMap.has(PHASE_SLUG.POST_CYCLE) ) allPhases.push(PHASE_SLUG.POST_CYCLE)

    // cicla e trasforma tutte le PHASES in gruppi di giorni misti adatti alla visualizzazione del CROP-RECIPE
    const groups: ExtendedGroupPhase[] = Array.from(allPhases).map(phase => {
        const recipePhaseDays = recipePhaseMap.get(phase) ?? []
        const cropPhaseDays = cropPhaseMap.get(phase) ?? []
        const schema = rearrangementSchema.find(s => s.phase === phase)
        const policy = recipe?.shrinkExpandDays?.find(p => p.phase === phase)

        // Determina il numero massimo di giorni tra Recipe e Crop
        const maxDays = Math.max(recipePhaseDays.length, cropPhaseDays.length, 1)

        // Crea i giorni combinati
        const combinedDays: CombinedDay[] = []
        for (let i = 0; i < maxDays; i++) {
            const recipeDay: Partial<CombinedDay> = recipePhaseDays[i] ?? null
            const cropDay = cropPhaseDays[i] ?? null

            combinedDays.push({
                nutrientSolutionId: recipeDay?.nutrientSolutionId,
                co2: recipeDay?.co2,
                ec: recipeDay?.ec,
                ph: recipeDay?.ph,
                humDay: recipeDay?.humDay,
                humNight: recipeDay?.humNight,
                tempDay: recipeDay?.tempDay,
                tempNight: recipeDay?.tempNight,
                irrigationDuration: recipeDay?.irrigationDuration,
                irrigationPause: recipeDay?.irrigationPause,
                ledBars: recipeDay?.ledBars ?? 0,
                dayPhotoPeriod: recipeDay?.dayPhotoPeriod ?? 0,
                nightPhotoPeriod: recipeDay?.nightPhotoPeriod ?? 0,
                spectrum: recipeDay?.spectrum ?? 0,

                // Aggiungi i task dal CropRecipe se disponibili
                tasks: cropDay?.taskTemplateGroupIds ?? []
            })
        }

        return {
            phase,
            collapsed: false,
            days: combinedDays as CombinedDay[],
            daysSelect: [],
            shrink: {
                name: policy?.shrinkPolicy ?? POLICY_NAMES.DEFAULT,
                dayRef: policy?.shrinkDay ?? 1,
            },
            expand: {
                name: policy?.expandPolicy ?? POLICY_NAMES.DEFAULT,
                dayRef: policy?.expandDay ?? 1,
            },
            // Aggiungi proprietà specifiche del CropRecipe
            ratio: schema?.ratio ?? 1,
            shrinkDays: schema?.shrinkDays ?? 0,
            expandDays: schema?.expandDays ?? 0,
        }
    })

    return groups
}

/** 
 * CROP: trasforma un array di "group-phase" in un array di "day" 
 */
export function cropPhasesGroupToDays(groups: ExtendedGroupPhase[] = []) {

    // crea i days
    const days: Day[] = groups.reduce((acc, group, groupIndex) => {
        const daysTasks = group.days.map((day, dayIndex) => ({
            phase: group.phase,
            taskTemplateGroupIds: day.tasks,
            position: +calcIndexDay(groups, groupIndex, dayIndex),
        }))
        return [...acc, ...daysTasks]
    }, [])

    // crea l'array di rearrangment
    const rearrangementSchema: Schema[] = groups.reduce((acc, group) => {
        if (group.phase == PHASE_SLUG.BATCH || group.phase == PHASE_SLUG.PRE_CYCLE || group.phase == PHASE_SLUG.POST_CYCLE) return acc
        return [...acc, {
            phase: group.phase,
            ratio: group.ratio,
            shrinkDays: group.shrinkDays,
            expandDays: group.expandDays
        }]
    }, [])

    return { days, rearrangementSchema }
}

/** 
 * RECIPE: trasforma un array di "group-phase" in un array di "day" 
 * */
export function recipePhasesGroupToDays(groups: ExtendedGroupPhase[] = [], common: CombinedDay = {}): Partial<Recipe> {
    
    const days = groups.reduce((acc, group) => {
        if (group.phase == PHASE_SLUG.PRE_CYCLE || group.phase == PHASE_SLUG.POST_CYCLE) return acc
        const daysProps = group.days.map((day, index) => {
            return {
                position: index + 1,
                phase: group.phase,
                nutrientSolutionId: common.nutrientSolutionId,
                co2: +day.co2,
                ec: +day.ec,
                ph: +day.ph,
                humDay: +day.humDay,
                humNight: +day.humNight,
                tempDay: +day.tempDay,
                tempNight: +day.tempNight,
                irrigationDuration: +day.irrigationDuration,
                irrigationPause: +day.irrigationPause,
                ledBars: +day.ledBars,
                dayPhotoPeriod: +day.dayPhotoPeriod,
                nightPhotoPeriod: +day.nightPhotoPeriod,
                spectrum: +day.spectrum,
            }
        })
        return [...acc, ...daysProps]
    }, [])

    const shrinkExpandDays: PhasePolicy[] = groups.map<PhasePolicy>(group => {
        if (group.phase == PHASE_SLUG.PRE_CYCLE || group.phase == PHASE_SLUG.POST_CYCLE) return null
        const shrinkPolicy = group.shrink?.name ?? POLICY_NAMES.DEFAULT
        const expandPolicy = group.expand?.name ?? POLICY_NAMES.DEFAULT
        const shrinkDay = shrinkPolicy == POLICY_NAMES.DEFAULT ? group.days.length : (group.shrink?.dayRef ?? 1)
        const expandDay = expandPolicy == POLICY_NAMES.DEFAULT ? group.days.length : (group.expand?.dayRef ?? 1)
        return {
            phase: group.phase,
            shrinkDay,
            expandDay,
            shrinkPolicy,
            expandPolicy,
        }
    }).filter(p => !!p)

    return { days, shrinkExpandDays }
}

// [string] restituisce un indice del giorno del CROP
// tenendo conto dei gruppi e della PHASE PRE-CYCLE e POST-CYCLE
export function calcIndexDay(groups: ExtendedGroupPhase[], groupIndex: number, dayIndex: number) {

    // calcolo dell'indice sui gruppi
    let count = groups
        .reduce((acc, group, index) => acc += index < groupIndex ? group.days.length : 0, 0)
    count = count + dayIndex + 1

    // se c'e' il gruppo PRE_CYCLE allora togli giorni
    let group = groups[0]
    if (group.phase == PHASE_SLUG.PRE_CYCLE) count -= group.days.length

    // se il gruppo che sto analizzando è PRE-CYCLE togli un giorno perche' deve finire con -1 e non con 0
    group = groups[groupIndex]
    if (group.phase == PHASE_SLUG.PRE_CYCLE) count -= 1

    let ret: string = count.toString()
    // se il gruppo che sto analizzando è POST-CYCLE agiungi "+"
    if (group.phase == PHASE_SLUG.POST_CYCLE) count = `+${count}` as any

    return count
}


/** restituisce i CROPS filtrati e ordinati in base alla querystring */
export function filterAndSortCrops(crops: Crop[]): Crop[] {
    if (!crops || crops.length == 0) return []

    const [txt, farmIdStr, tagsStr] = querySo.getSearchUrl(["search", "farm", "tags"])
    const chk = { "archived": (querySo.getSearchUrl("archived") == "true") }

    let items = getItemsFiltered(
        crops,
        { txt, withArchived: chk.archived, onlyArchived: chk.archived, noSort: true, farmId: farmIdStr ? +farmIdStr : null, tags: tagsStr?.split("~") }
    ) as Crop[]
    items = querySo.getSorted({ items })
    return items
}

/** restituisce un nuovo "day" o completa quello del parametro */
export function getNewDay(day: Partial<CombinedDay> = {}): CombinedDay {
    return {
        nutrientSolutionId: null, // int-id
        co2: 0,
        ec: 0.0,
        ph: 0.0,
        humDay: 0,
        humNight: 0,
        tempDay: 0,
        tempNight: 0,
        irrigationDuration: 0,
        irrigationPause: 0,
        ledBars: 0,
        dayPhotoPeriod: 0,
        nightPhotoPeriod: 0,
        spectrum: 0,

        tasks: [],
        ...day
    }
}

// il "groupDay" del è del tipo che necessita del parametro "ratio"
export function isRealPhase(phase: string): boolean {
    return phase != PHASE_SLUG.PRE_CYCLE && phase != PHASE_SLUG.POST_CYCLE && phase != PHASE_SLUG.BATCH
}